import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  WarningAmberOutlined,
  CheckCircleOutlineOutlined,
  LogoutOutlined,
  BlockOutlined,
  SaveOutlined,
  RestoreOutlined,
} from '@mui/icons-material';
import type { UserVM } from '@tto/shared-models';
import { useDeactivateUserMutation, useReinstateUserMutation } from '../../../services/restApi/queries/users.service';
import { toast } from 'react-toastify';

// ============================================================================
// Deactivate User Dialog
// ============================================================================

interface DeactivateUserDialogProps {
  user: UserVM | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const DeactivateUserDialog = ({ user, open, onClose, onSuccess }: DeactivateUserDialogProps) => {
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [deactivateUser, { isLoading }] = useDeactivateUserMutation();

  const handleClose = () => {
    if (!isLoading) {
      setConfirmText('');
      setReason('');
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!user || confirmText !== 'DEACTIVATE') return;

    try {
      // await deactivateUser({
      // 	userId: user.id,
      // 	input: { reason: reason || undefined },
      // }).unwrap();
      toast.success(`${user.firstName} ${user.lastName} has been deactivated`);
      handleClose();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = error?.data || error?.message || 'Failed to deactivate user';
      toast.error(errorMessage);
    }
  };

  if (!user) return null;

  const fullName = `${user.firstName} ${user.lastName}`;
  const isConfirmValid = confirmText === 'DEACTIVATE';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>
        <Box display='flex' alignItems='center' gap={1}>
          <WarningAmberOutlined color='warning' />
          <Typography variant='h6' component='div'>
            Deactivate {fullName}?
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity='warning' sx={{ mb: 3 }}>
          This will immediately revoke {fullName}'s access to the platform.
        </Alert>

        <Typography variant='subtitle2' fontWeight={600} mb={1.5}>
          What happens when you deactivate a user:
        </Typography>

        <List dense>
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <LogoutOutlined fontSize='small' />
            </ListItemIcon>
            <ListItemText primary='They will be logged out of all active sessions' />
          </ListItem>
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <BlockOutlined fontSize='small' />
            </ListItemIcon>
            <ListItemText primary='They will not be able to log in' />
          </ListItem>
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <SaveOutlined fontSize='small' />
            </ListItemIcon>
            <ListItemText primary='Their data will be preserved' />
          </ListItem>
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <RestoreOutlined fontSize='small' />
            </ListItemIcon>
            <ListItemText primary='You can reinstate them at any time' />
          </ListItem>
        </List>

        <TextField
          label='Reason (optional)'
          multiline
          rows={3}
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder='Provide a reason for deactivating this user...'
          disabled={isLoading}
          sx={{ mb: 3 }}
        />

        <Typography variant='body2' fontWeight={600} mb={1}>
          Type <strong>DEACTIVATE</strong> to confirm
        </Typography>
        <TextField
          fullWidth
          placeholder='DEACTIVATE'
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
          disabled={isLoading}
          error={confirmText.length > 0 && !isConfirmValid}
          helperText={confirmText.length > 0 && !isConfirmValid ? 'Text must match exactly' : ''}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading} color='inherit'>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant='contained'
          color='warning'
          disabled={!isConfirmValid || isLoading}
          startIcon={isLoading && <CircularProgress size={16} />}
        >
          {isLoading ? 'Deactivating...' : 'Deactivate User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// Reinstate User Dialog
// ============================================================================

interface ReinstateUserDialogProps {
  user: UserVM | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ReinstateUserDialog = ({ user, open, onClose, onSuccess }: ReinstateUserDialogProps) => {
  const [reinstateUser, { isLoading }] = useReinstateUserMutation();

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!user) return;

    try {
      await reinstateUser({
        userId: user.id,
        input: {},
      }).unwrap();
      toast.success(`${user.firstName} ${user.lastName} has been reinstated`);
      handleClose();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = error?.data || error?.message || 'Failed to reinstate user';
      toast.error(errorMessage);
    }
  };

  if (!user) return null;

  const fullName = `${user.firstName} ${user.lastName}`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>
        <Box display='flex' alignItems='center' gap={1}>
          <CheckCircleOutlineOutlined color='success' />
          <Typography variant='h6' component='div'>
            Reinstate {fullName}?
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant='body1' mb={2}>
          This will restore {fullName}'s access to the platform.
        </Typography>

        <Alert severity='info' variant='outlined'>
          <Typography variant='body2'>
            The user will be able to log in immediately with their previous password.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading} color='inherit'>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant='contained'
          color='success'
          disabled={isLoading}
          startIcon={isLoading && <CircularProgress size={16} />}
        >
          {isLoading ? 'Reinstating...' : 'Reinstate User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
